/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import PayerForm from "components/forms/PayerForm";
import Box from "components/generals/Box";
import Button from "components/generals/Button";
import ButtonDialog from "components/generals/ButtonDialog";
import DefaultCell from "components/generals/DefaultCell";
import { Modal } from "components/generals/Modal";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { CoreAPI_URLs } from "constants/http.const";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import useHttp from "hooks/http.hook";
import { TableProps } from "interfaces";
import { HeaderEnum, PayerInterface } from "@malmur-technologies/medicalsystem-shared";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { getNewPayerDataContent } from "utils/model.utils";

/**
 * Payer table component for displaying and managing insurance payer records.
 * 
 * Features:
 * - Displays payer information in sortable data table
 * - Columns: name, last name, phone, state, action buttons
 * - Edit payer functionality via modal with PayerForm
 * - Delete payer with confirmation dialog and list update
 * - Permission-based access control for all operations
 * - Sortable by name, phone, and state
 * 
 * Payer Types:
 * - Insurance companies
 * - Government programs (Medicare, Medicaid)
 * - Workers' compensation
 * - Private insurers
 * - Self-pay/guarantors
 * 
 * Security:
 * - Uses HeaderEnum.RESOURCE_ID for secure identification
 * - Permission-based rendering via PermissionWrapper
 * - Validates entity permissions before delete operations
 * 
 * @param {TableProps<PayerInterface>} props - Table properties
 * @param {PayerInterface[]} props.modelList - Array of payer records
 * @param {Function} props.setModelList - Callback to update payer list
 * 
 * @returns {JSX.Element} Payer data table with edit/delete actions
 */
const PayerTable: React.FC<TableProps<PayerInterface>> = ({
  modelList,
  setModelList,
  ...props
}) => {
  const [selectedPayer, setSelectedPayer] = useState<PayerInterface>(getNewPayerDataContent());
  const [activeEditPayerModal, setActiveEditPayerModal] = useState<boolean>(false);
  const [modalAction, setModalAction] = useState<FormActionType>();
  const { t } = useTranslation();

  const [_isLoading, _error, sendRequest] = useHttp();

  const openModalHandler = (payer: PayerInterface) => {
    setSelectedPayer(payer);
    setActiveEditPayerModal(true);
  };

  const deleteHandler = (payerTemplate: PayerInterface) => {
    setSelectedPayer(payerTemplate);

    sendRequest<boolean, PayerInterface>(
      CoreAPI_URLs.PAYER_GENERAL_OPERATIONS,
      onPayerTemplateDeleteHandler,
      "DELETE",
      JSON.stringify(payerTemplate),
      [{ header: HeaderEnum.RESOURCE_ID, value: payerTemplate.id }],
      { payload: payerTemplate, successMessage: t("payerTable.payerDeleted") }
    );
  };

  const onPayerTemplateDeleteHandler = (result: boolean, payerTemplate: PayerInterface) => {
    const indexList = modelList.indexOf(payerTemplate);
    modelList.splice(indexList, 1);
    setModelList([...modelList]);
  };

  const getUITable = (list: PayerInterface[]) => {
    return {
      columns: [
        {
          Header: t("payerTable.name"),
          accessor: "name",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("payerTable.lastname"),
          accessor: "lastname",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("payerTable.phone"),
          accessor: "phone",
          sortIndicatorType: "numeric",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("payerTable.state"),
          accessor: "state",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("payerTable.actions"),
          accessor: "id",
          disableSortBy: true,
          Cell: ({ value }: any) => (
            <>
              <Button
                name="viewPayerButton"
                variant="contained"
                color="info"
                onClick={() => {
                  const payer = list.find((p) => p.id === value);
                  if (payer) {
                    setModalAction(FormActionType.MODIFY);
                    openModalHandler(payer);
                  }
                }}
              >
                {t("payerTable.viewAction")}
              </Button>
              <PermissionWrapper
                requiredPermissions={[PermissionsUser[EntityPermissions.PAYER].DELETE]}
              >
                <Box sx={{ ml: 1, display: "inline" }}>
                  <ButtonDialog
                    name="deletePayerButton"
                    onAcceptHandle={() => {
                      const payer = list.find((p) => p.id === value);
                      if (payer) {
                        deleteHandler(payer);
                      }
                    }}
                    buttonText={t("payerTable.deleteAction")}
                    title={t("payerTable.deleteConfirmation")}
                    description={t("payerTable.description")}
                    labelledbyID={"PayerDelete"}
                    describedbyID={"PayerDescriptionId"}
                  />
                </Box>
              </PermissionWrapper>
            </>
          ),
        },
      ],
      rows: list,
    };
  };

  const dataTableData = useMemo(() => getUITable(modelList), [modelList]);
  return (
    <>
      <Box mt={2} mb={2} width="100%" display="flex" justifyContent="flex-end">
        <PermissionWrapper requiredPermissions={[PermissionsUser[EntityPermissions.PAYER].CREATE]}>
          <Button
            name="newPayerButton"
            type="button"
            variant="contained"
            color="primary"
            onClick={() => {
              setModalAction(FormActionType.NEW);
              setSelectedPayer(getNewPayerDataContent());
              setActiveEditPayerModal(true);
            }}
          >
            {t("payerForm.newPayer")}
          </Button>
        </PermissionWrapper>
      </Box>
      <DataTable {...props} table={dataTableData} canSearch isLoading={props.isLoading}
        onSortChange={props.onSortChange}
        currentSortBy={props.currentSortBy}
        currentSortOrder={props.currentSortOrder}
      />
      <Modal
        minWidth="500px"
        maxWidth="900px"
        open={activeEditPayerModal}
        onClose={() => {
          setActiveEditPayerModal(false);
        }}
      >
        <PayerForm
          modelList={modelList}
          setModelList={setModelList}
          action={modalAction}
          model={selectedPayer}
          onCloseAction={() => {
            setActiveEditPayerModal(false);
          }}
          isModal={true}
        />
      </Modal>
    </>
  );
};

export default PayerTable;
